import React, { useState, useCallback } from 'react';
import { useAsync } from 'react-use';

import { stringifyEntityRef } from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

import {
  TextField,
  makeStyles,
  InputAdornment,
  CircularProgress,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Autocomplete } from '@material-ui/lab';

import { OwnershipEntity } from './types';

const useStyles = makeStyles(() => ({
  autocomplete: {
    '& .MuiInputBase-root': {
      fontSize: 13,
    },
  },
}));

interface EntityOption {
  id: string;
  name: string;
  title?: string;
  entityRef: string;
  entityLink: string;
  currentOwner?: string;
  annotations?: Record<string, string>;
}

interface EntitySelectorProps {
  kind: string;
  excludeIds: Set<string>;
  onSelect: (entity: OwnershipEntity) => void;
  disabled?: boolean;
  filterFn?: (entity: EntityOption) => boolean;
}

/**
 * Entity selector for adding entities to the ownership dialog.
 * Searches entities of a specific kind and converts them to OwnershipEntity.
 */
export const EntitySelector = ({
  kind,
  excludeIds,
  onSelect,
  disabled,
  filterFn,
}: EntitySelectorProps) => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const [search, setSearch] = useState<string>('');
  const [options, setOptions] = useState<EntityOption[]>([]);
  const [inputValue, setInputValue] = useState('');

  const { loading } = useAsync(async () => {
    if (!search || search.length < 2) {
      setOptions([]);
      return;
    }

    const queryEntitiesResponse = await catalogApi.queryEntities({
      fullTextFilter: {
        term: search,
        fields: ['metadata.name', 'metadata.title'],
      },
      filter: {
        kind: [kind],
      },
      limit: 20,
    });

    const newOptions = queryEntitiesResponse.items
      .filter(item => !excludeIds.has(item.metadata.uid || item.metadata.name))
      .map(item => {
        const entityRef = stringifyEntityRef(item);
        const namespace = item.metadata.namespace || 'default';
        const entityLink = `/catalog/${namespace}/${item.kind.toLowerCase()}/${
          item.metadata.name
        }`;

        return {
          id: item.metadata.uid || item.metadata.name,
          name: item.metadata.name,
          title: item.metadata.title,
          entityRef,
          entityLink,
          currentOwner: item.spec?.owner as string | undefined,
          annotations: item.metadata.annotations as
            | Record<string, string>
            | undefined,
        };
      })
      .filter(option => !filterFn || filterFn(option));

    setOptions(newOptions);
  }, [search, kind, excludeIds, filterFn]);

  const handleChange = useCallback(
    (_: any, selected: EntityOption | null) => {
      if (selected) {
        onSelect({
          id: selected.id,
          name: selected.name,
          kind,
          entityRef: selected.entityRef,
          displayName: selected.title || selected.name,
          entityLink: selected.entityLink,
          currentOwner: selected.currentOwner,
          annotations: selected.annotations,
        });

        // Clear the input after selection
        setInputValue('');
        setSearch('');
        setOptions([]);
      }
    },
    [onSelect, kind],
  );

  const handleInputChange = useCallback(
    (_: any, newInputValue: string, reason: string) => {
      setInputValue(newInputValue);
      if (reason === 'input' && newInputValue.length >= 2) {
        setSearch(newInputValue);
      } else if (reason === 'clear') {
        setSearch('');
        setOptions([]);
      }
    },
    [],
  );

  return (
    <Autocomplete
      className={classes.autocomplete}
      options={options}
      value={null}
      inputValue={inputValue}
      disabled={disabled}
      loading={loading}
      getOptionLabel={option => option.title || option.name}
      getOptionSelected={(option, selected) => option.id === selected.id}
      onChange={handleChange}
      onInputChange={handleInputChange}
      popupIcon={<ExpandMoreIcon />}
      clearOnBlur
      blurOnSelect
      renderInput={params => (
        <TextField
          {...params}
          variant="outlined"
          size="small"
          placeholder={`Search for a ${kind.toLowerCase()} to add...`}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <InputAdornment position="end">
                    <CircularProgress size={16} />
                  </InputAdornment>
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={option => (
        <span>
          {option.title || option.name}
          {option.title && option.title !== option.name && (
            <span style={{ marginLeft: 8, opacity: 0.6, fontSize: 12 }}>
              ({option.name})
            </span>
          )}
        </span>
      )}
    />
  );
};
